import { NetworkError, type HttpClient } from '@/model';
import type { HttpAuth } from './http';
import { initialProtocol } from './protocol';

/** Browser dictation admission: docs/api/v1/web.md. No bearer token or client credential enters a URL. */
export function createSocketOpener(base: string, auth: HttpAuth | undefined, protocol: () => number = () => initialProtocol): NonNullable<HttpClient['openWebSocket']> {
  return async (path, options = {}) => {
    const generation = auth?.generation?.();
    const target = new URL(path, base);
    if (target.origin !== new URL(base).origin || target.pathname !== '/v1/asr/stream' || target.username || target.password || target.hash) throw new NetworkError('Unsupported socket destination');
    for (const [key, value] of Object.entries(options.query ?? {})) if (value !== undefined) {
      if (target.searchParams.has(key)) throw new NetworkError('Duplicate socket declaration');
      target.searchParams.set(key, String(value));
    }
    if ([...target.searchParams.keys()].some(key => !['mode', 'protocol'].includes(key) || target.searchParams.getAll(key).length !== 1)) throw new NetworkError('Unsupported socket declaration');
    if (options.protocol !== undefined && target.searchParams.has('protocol')) throw new NetworkError('Duplicate socket protocol');
    const declared = options.protocol ?? Number(target.searchParams.get('protocol') ?? protocol());
    if (!Number.isSafeInteger(declared) || declared < 3 || declared > protocol()) throw new NetworkError('Unsupported socket protocol');
    const mode = target.searchParams.get('mode'); if (mode !== null && mode !== 'stream' && mode !== 'batch') throw new NetworkError('Unsupported dictation mode');
    target.searchParams.set('protocol', String(declared));
    target.protocol = target.protocol === 'https:' ? 'wss:' : 'ws:';
    const token = auth?.accessToken ? await auth.accessToken() : auth?.token();
    if (!token || generation !== auth?.generation?.() || options.signal?.aborted) throw new NetworkError('This account session has ended');
    return new Promise<WebSocket>((resolve, reject) => {
      const socket = new WebSocket(target.toString(), ['hylo.v1', `hylo.bearer.${token}`]);
      let opened = false;
      const ended = () => { socket.close(1000); if (!opened) reject(new NetworkError('This account session has ended')); };
      const retire = auth?.onRetire?.(ended);
      options.signal?.addEventListener('abort', ended, { once: true });
      socket.addEventListener('open', () => {
        if (generation !== auth?.generation?.() || options.signal?.aborted || socket.protocol !== 'hylo.v1') { ended(); return; }
        opened = true; resolve(socket);
      }, { once: true });
      socket.addEventListener('error', () => { if (!opened) reject(new NetworkError('Unable to open dictation')); }, { once: true });
      socket.addEventListener('close', () => { retire?.(); options.signal?.removeEventListener('abort', ended); if (!opened) reject(new NetworkError('Dictation admission was refused')); }, { once: true });
    });
  };
}
